import { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { IoIosEyeOff, IoIosEye } from "react-icons/io";
import "react-phone-input-2/lib/style.css";

import { showToast } from "../../utils/toast";
import Button from "../../components/Button";
import TextInput from "../../components/TextInput";
import CountryCodeSelect from "../../components/CountryCodeSelect";
import {
  newPasswordValidation,
  PASSWORD_REQUIREMENTS_HINT,
} from "../../schemas/password";
import { apiClient } from "../../services/api";
import {
  ApiRoutes,
  token,
  refreshToken,
  Terms,
  PrivacyPolicy,
  routePaths,
} from "../../constants";
import { useAppState, RESTORE_SESSION } from "../../contexts/AppContext";
import { getUserRoleFromToken } from "../../utils/user";
import { UserRole } from "../../services/queries/users/types";
import { parseError, AppRoutes } from "../../utils";

export const Route = createFileRoute("/_auth/signup")({
  component: SignupPage,
});

interface FormProps {
  businessName: string;
  country: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  password: string;
  diallingCode: string;
}

const countries = [
  //should come from api
  { label: "Nigeria", value: "NG" },
  { label: "Gambia", value: "GM" },
  { label: "Ghana", value: "GH" },
  { label: "Kenya", value: "KE" },
  { label: "Uganda", value: "UG" },
  { label: "Tanzania", value: "TZ" },
];

function SignupPage() {
  const { dispatch, state } = useAppState();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormProps>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      diallingCode: "+234",
    },
  });

  const onSubmit: SubmitHandler<FormProps> = async ({
    email,
    password,
    firstName,
    lastName,
    businessName,
    phone,
    diallingCode,
    country,
  }) => {
    setIsLoading(true);

    try {
      const { data } = await apiClient.post({
        url: ApiRoutes.SIGNUP,
        body: {
          email,
          password,
          firstName,
          lastName,
          name: businessName,
          phone,
          diallingCode: diallingCode.replace("+", ""),
          countryCode: country,
        },
      });

      Cookies.set(token, data.accessToken);
      Cookies.set(refreshToken, data.refreshToken);

      const businessUserRole = getUserRoleFromToken(data.accessToken);

      dispatch({
        type: RESTORE_SESSION,
        payload: {
          authToken: data.accessToken,
          businessUserRole: businessUserRole as UserRole,
          user: data.user,
          business: data.business,
        },
      });

      showToast("Signup successful", "success");
    } catch (error: unknown) {
      const message = parseError(error);
      showToast(message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!state.user?.id || !state.business?.id) return;
    navigate({ to: AppRoutes.DASHBOARD });
  }, [state.user?.id, state.business?.id, navigate]);

  return (
    <div>
      <h1 className="uppercase text-center text-gray-600">
        create your account
      </h1>

      <form className="my-6" onSubmit={handleSubmit(onSubmit)}>
        <div className="form-control mb-5 space-x-0">
          <label
            htmlFor="country"
            className="block w-100 tracking-wide text-gray-700 text-sm font-bold mb-2"
          >
            Country
          </label>

          <select
            id="country"
            className="w-full px-2 py-3 rounded-lg border"
            {...register("country", {
              required: "Country is required",
            })}
          >
            {countries.map((country) => (
              <option key={country.value} value={country.value}>
                {country.label}
              </option>
            ))}
          </select>

          {errors.country && (
            <p className="text-red-500 text-xs mt-1">
              {errors.country?.message}
            </p>
          )}
        </div>

        <div className="form-control mb-5 space-x-0">
          <label
            htmlFor="businessName"
            className="block w-100 tracking-wide text-gray-700 text-sm font-bold mb-2"
          >
            Business Name
          </label>

          <TextInput
            className="w-full px-6 py-3 rounded-lg"
            {...register("businessName", {
              required: "Business Name is required",
            })}
            error={errors.businessName}
          />
        </div>

        <div className="form-control mb-5">
          <label
            htmlFor="firstName"
            className="block tracking-wide text-gray-700 text-sm font-bold mb-2"
          >
            First Name
          </label>

          <TextInput
            className="md:w-full px-6 py-3 rounded-lg"
            {...register("firstName", {
              required: "First Name is required",
            })}
            error={errors.firstName}
          />
        </div>

        <div className="form-control mb-5">
          <label
            htmlFor="lastName"
            className="block w-100 tracking-wide text-gray-700 text-sm font-bold mb-2"
          >
            Last Name
          </label>

          <TextInput
            className="w-full px-6 py-3 rounded-lg"
            {...register("lastName", { required: "Last Name is required" })}
            error={errors.lastName}
          />
        </div>

        <div className="form-control mb-5">
          <label
            htmlFor="email"
            className="block w-100 tracking-wide text-gray-700 text-sm font-bold mb-2"
          >
            Email Address
          </label>

          <TextInput
            className=" px-6 py-3 rounded-lg"
            {...register("email", {
              required: "Business Email Address is required",
            })}
            error={errors.email}
          />
        </div>

        <div className="form-control mb-5">
          <label
            htmlFor="phone"
            className="block w-100 tracking-wide text-gray-700 text-sm font-bold mb-2"
          >
            Phone Number
          </label>

          <div className="flex gap-2">
            <Controller
              name="diallingCode"
              control={control}
              rules={{ required: "Dialling Code is required" }}
              render={({ field: { onChange, value } }) => (
                <CountryCodeSelect
                  value={value}
                  error={errors.diallingCode}
                  onChange={(dialCode, countryCode) =>
                    onChange(dialCode, countryCode)
                  }
                />
              )}
            />

            <TextInput
              className=" px-6 py-3 rounded-lg"
              {...register("phone", {
                required: "Phone Number is required",
              })}
              error={errors.phone}
              onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                e.target.value = e.target.value.replace(/[^0-9]/g, "");
              }}
            />
          </div>
        </div>

        <div className="form-control mb-8">
          <label
            htmlFor="password"
            className="block w-100 tracking-wide text-gray-700 text-sm font-bold mb-2"
          >
            Password
          </label>

          <div className="relative">
            <TextInput
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              aria-describedby="password-hint"
              className="px-6 py-3 rounded-lg"
              {...register("password", newPasswordValidation)}
              error={errors.password}
            />

            <button
              type="button"
              onClick={togglePasswordVisibility}
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-controls="password"
              className="absolute right-0 top-0 pr-3 pt-4 flex items-center cursor-pointer"
            >
              {showPassword ? <IoIosEye /> : <IoIosEyeOff />}
            </button>
          </div>

          <p id="password-hint" className="text-gray-500 text-xs mt-1">
            {PASSWORD_REQUIREMENTS_HINT}
          </p>
        </div>

        <div className="flex justify-center md:flex-row flex-col mt-8 w-full">
          <Button
            className="bg-primary hover:bg-primary/80 transition-colors text-white py-3 hover:border-accent rounded-lg border-primary w-full"
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? "Creating..." : "Create my account"}
          </Button>
        </div>

        <p className="text-center text-gray-600 mt-4">
          Already have an account?{" "}
          <Link to={routePaths.login}>
            <span className="text-primary"> Login</span>
          </Link>
        </p>

        <div className="terms">
          <p className="text-sm my-6 text-gray-500">
            By creating an account, you agree to our{" "}
            <a href={Terms} target="_blank" rel="noopener noreferrer">
              <span className="text-primary text-sm">Terms of Service</span>
            </a>{" "}
            and{" "}
            <a href={PrivacyPolicy} target="_blank" rel="noopener noreferrer">
              <span className="text-primary text-sm">Privacy Policy</span>.
            </a>
          </p>
        </div>
      </form>
    </div>
  );
}
